import React, { useEffect, useState } from 'react';
import { Col, Collapse, Input, InputGroup, InputGroupText, Row } from 'reactstrap';

import { InputProps } from '~components/form-fields';
import * as FormInput from '~components/form-fields/inputs';
import { stripDecimalString } from '~components/form-fields/libs/numericFormatters';

interface ChargeableCancellationDetails {
  client: number | '';
  client_unit: 'mins' | 'fixed';
  carer: number | '';
}

// Strip all non-digit chars (integer only)
const stripInteger = (input: string): string => input.replace(/[^\d]/g, '');

// Coerce to integer minutes, minimum 0
const toIntMinutes = (v: string): number => Math.max(0, Math.round(parseFloat(v || '0')) || 0);

// Coerce to 2dp dollars, minimum 0
const toFixedDollars = (v: string): number =>
  Math.max(0, parseFloat(parseFloat(v || '0').toFixed(2)) || 0);

function FormInputChargeableCancellation({ value = {}, onChange }: InputProps) {
  const { chargeable_cancellation, chargeable_cancellation_details, duration } = value;
  const { client, client_unit, carer } = (chargeable_cancellation_details ?? {}) as Partial<ChargeableCancellationDetails>;
  const isOpen = chargeable_cancellation === true;
  const isFixed = client_unit === 'fixed';

  const chargeableTypes = [
    { id: 'mins', name: 'Mins' },
    { id: 'fixed', name: 'Fixed Amount' },
  ];

  // Local controlled string state — allows in-progress typing like "12."
  const [clientInput, setClientInput] = useState<string>(client != null ? String(client) : '');
  const [carerInput, setCarerInput] = useState<string>(carer != null ? String(carer) : '');

  // Sync local state when the upstream value changes (e.g. form reset).
  // In fixed mode, display the committed number at 2dp so trailing/whole-dollar
  // zeros aren't dropped (12.50 stays "12.50", 5 shows "5.00"). Storage stays
  // numeric — this is display-only. isFixed is a dep so a mins→fixed toggle
  // repaints even when the numeric value is unchanged.
  useEffect(() => {
    if (client == null || client === '') {
      setClientInput('');
      return;
    }
    setClientInput(isFixed ? Number(client).toFixed(2) : String(client));
  }, [client, isFixed]);

  useEffect(() => {
    setCarerInput(carer != null ? String(carer) : '');
  }, [carer]);

  useEffect(() => {
    // Load default cancellation details
    if (isOpen && !chargeable_cancellation_details) {
      // Seed defaults through the same minutes coercion the blur path uses, so
      // the initial state can't carry negative/fractional values.
      const dur = toIntMinutes(String(duration ?? ''));
      onChange &&
        onChange('chargeable_cancellation_details', {
          client: dur,
          client_unit: 'mins',
          carer: dur,
        });
    }
  }, [chargeable_cancellation]);

  useEffect(() => {
    // Clear cancellation details when chargeable cancellation is disabled
    if (!isOpen && chargeable_cancellation_details) {
      onChange && onChange('chargeable_cancellation_details', null);
    }
  }, [isOpen]);

  const setChange = (target: { name: string; value: any }) => {
    const { name, value } = target;
    const newValue = {
      ...chargeable_cancellation_details,
      [name]: value,
    };

    onChange && onChange('chargeable_cancellation_details', newValue);
  };

  // Client input handlers — unit-aware coercion
  const handleClientChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setClientInput(isFixed ? stripDecimalString(e.target.value) : stripInteger(e.target.value));
  };

  const handleClientBlur = () => {
    const num = isFixed ? toFixedDollars(clientInput) : toIntMinutes(clientInput);
    setChange({ name: 'client', value: num });
  };

  // Carer input handlers — always integer minutes
  const handleCarerChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setCarerInput(stripInteger(e.target.value));

  const handleCarerBlur = () => setChange({ name: 'carer', value: toIntMinutes(carerInput) });

  // Unit switch — reformat client value in one merged write to avoid stale-state race
  const handleUnitChange = (_name: string, newUnit: 'mins' | 'fixed') => {
    const reformatted =
      newUnit === 'fixed' ? toFixedDollars(clientInput) : toIntMinutes(clientInput);
    onChange &&
      onChange('chargeable_cancellation_details', {
        ...chargeable_cancellation_details,
        client_unit: newUnit,
        client: reformatted,
      });
  };

  const labelSm = 4;

  return (
    <>
      <FormInput.Switch
        type="switch"
        id="chargeable_cancellation"
        name="chargeable_cancellation"
        value={chargeable_cancellation ?? ''}
        onChange={onChange}
      />
      <Collapse isOpen={isOpen}>
        <Row className="mb-1 align-items-center">
          <Col sm={labelSm}>Charge Client</Col>
          <Col className="d-flex align-items-center">
            {isFixed ? (
              <InputGroup className="w-25 me-1">
                <InputGroupText>$</InputGroupText>
                <Input
                  type="text"
                  inputMode="decimal"
                  name="client"
                  id="client"
                  value={clientInput}
                  onChange={handleClientChange}
                  onBlur={handleClientBlur}
                  data-cy="client-input"
                />
              </InputGroup>
            ) : (
              <Input
                type="text"
                inputMode="numeric"
                name="client"
                id="client"
                value={clientInput}
                className="w-25 me-1"
                onChange={handleClientChange}
                onBlur={handleClientBlur}
                data-cy="client-input"
              />
            )}
            <FormInput.Select
              type="select"
              name="client_unit"
              id="client_unit"
              className="w-50"
              value={client_unit ?? ''}
              ignorePlaceholder={true}
              onChange={handleUnitChange}
              options={chargeableTypes}
            />
          </Col>
        </Row>
        <Row className="mb-1 align-items-center">
          <Col sm={labelSm}>Pay Care Worker</Col>
          <Col className="d-flex align-items-center">
            <Input
              type="text"
              inputMode="numeric"
              name="carer"
              id="carer"
              value={carerInput}
              className="w-25 me-1"
              onChange={handleCarerChange}
              onBlur={handleCarerBlur}
              data-cy="carer-input"
            />
            <span className="ms-2">mins</span>
          </Col>
        </Row>
      </Collapse>
    </>
  );
}

export default FormInputChargeableCancellation;
